import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X, Edit2, Check, AlertTriangle } from 'lucide-react';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { puoFare } from '../../utils/permessi';

const fmt = (n) => `€${(n||0).toFixed(2)}`;

export default function Orders() {
  const { user } = useAuth();
  const puoCreare     = puoFare(user, 'ordini', 'create');
  const puoModificare = puoFare(user, 'ordini', 'edit');
  const [orders, setOrders]     = useState([]);
  const [stati, setStati]       = useState([]);
  const [products, setProducts] = useState([]);
  const [filter, setFilter]     = useState('');
  const [selected, setSelected] = useState(null);
  const [payModal, setPayModal] = useState(null);
  const [payForm, setPayForm]   = useState({ metodo:'CONTANTI', importo:'', note:'' });
  const [editMode, setEditMode] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [or, st] = await Promise.all([api.get('/orders'), api.get('/order-statuses')]);
      setOrders(or.data);
      setStati(st.data);
      if (selected) setSelected(or.data.find(o => o.id === selected.id) || null);
    } catch(e) { console.error(e); }
  };

  useEffect(() => {
    load();
    api.get('/products').then(r => setProducts(r.data)).catch(()=>{});
  }, []);

  const getStatoColor = (nome) => stati.find(s => s.nome === nome)?.colore || 'var(--text-dim)';
  const getStatiDisponibili = (statoCorrente) => {
    // Tutti gli stati tranne quello corrente e quelli finali già raggiunti
    const corrente = stati.find(s => s.nome === statoCorrente);
    if (corrente?.is_finale) return [];
    return stati.filter(s => s.nome !== statoCorrente && s.attivo);
  };
  const statoIniziale = stati.find(s => s.is_iniziale)?.nome || 'BOZZA';

  const filtered = filter ? orders.filter(o => o.stato === filter) : orders;
  const totalePagato = (o) => o.payments?.reduce((s,p) => s+p.importo, 0) || 0;

  const changeStatus = async (id, stato) => {
    try { await api.patch(`/orders/${id}/status`, { stato }); load(); }
    catch(err) { alert(err.response?.data?.error || 'Errore cambio stato'); }
  };

  const handlePay = async (e) => {
    e.preventDefault();
    try { await api.post(`/orders/${payModal.id}/payment`, payForm); setPayModal(null); load(); }
    catch(err) { alert(err.response?.data?.error || 'Errore'); }
  };

  const startEdit = () => {
    if (selected.stato !== statoIniziale) { alert(`Puoi modificare solo ordini in stato ${statoIniziale}`); return; }
    setEditItems(selected.items.map(item => ({
      variant_id: String(item.variant_id),
      product_id: String(item.variant?.product?.id || ''),
      quantita: item.quantita, note: item.note || '',
    })));
    setEditMode(true);
  };

  const saveEdit = async () => {
    try {
      await api.put(`/orders/${selected.id}`, {
        items: editItems.map(i => ({ variant_id: parseInt(i.variant_id), quantita: parseInt(i.quantita), note: i.note||null }))
      });
      setEditMode(false); load();
    } catch(err) { alert(err.response?.data?.error || 'Errore'); }
  };

  const getVariants = (pid) => products.find(p => p.id === parseInt(pid))?.variants?.filter(v=>v.attiva) || [];
  const upd = (idx,k,v) => setEditItems(p => p.map((it,i) => i===idx ? {...it,[k]:v} : it));

  return (
    <div style={{ display:'flex', gap:'20px' }}>

      {/* ── LISTA ── */}
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'20px' }}>
          <div>
            <h1 style={s.title}>ORDINI & VENDITE</h1>
            <p style={s.sub}>{orders.length} ordini totali</p>
          </div>
          {puoCreare && <button style={s.btnPri} onClick={() => navigate('/orders/new')}><Plus size={15}/> NUOVO ORDINE</button>}
        </div>

        {/* Filtri per stato */}
        <div style={{ display:'flex', gap:'6px', marginBottom:'16px', flexWrap:'wrap' }}>
          <button onClick={() => setFilter('')} style={{ ...s.filterBtn, ...(filter==='' ? s.filterActive : {}) }}>
            TUTTI ({orders.length})
          </button>
          {stati.map(st => {
            const count = orders.filter(o => o.stato === st.nome).length;
            if (!count) return null;
            return (
              <button key={st.id} onClick={() => setFilter(st.nome)}
                style={{ ...s.filterBtn, borderColor:`${st.colore}50`, color: filter===st.nome ? st.colore : '#777', background: filter===st.nome ? `${st.colore}18` : 'var(--bg-card)' }}>
                {st.nome} ({count})
              </button>
            );
          })}
        </div>

        {/* Righe ordine */}
        <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
          {filtered.map(o => {
            const pagato = totalePagato(o);
            const saldo = o.totale - pagato;
            const colore = getStatoColor(o.stato);
            const isOpen = selected?.id === o.id;
            const hasWarnings = o.history?.some(h => h.tipo === 'ALERT');
            return (
              <div key={o.id} style={{ ...s.orderRow, ...(isOpen ? s.orderRowSel : {}) }}
                onClick={() => { setSelected(isOpen ? null : o); setEditMode(false); }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'3px' }}>
                    <span style={{ fontFamily:"'Barlow Condensed'", fontWeight:'800', fontSize: 'var(--font-size-lg)' }}>{o.numero_ordine}</span>
                    <span style={{ ...s.badge, background:`${colore}18`, color:colore, borderColor:`${colore}40` }}>{o.stato}</span>
                    {hasWarnings && <AlertTriangle size={13} color="#F59E0B" />}
                  </div>
                  <div style={{ color:'var(--text-muted)', fontSize:'var(--font-size-sm)' }}>{o.customer?.cognome} {o.customer?.nome}</div>
                  <div style={{ color:'var(--text-dim)', fontSize:'var(--font-size-xs)', marginTop:'2px' }}>{o.items?.length} articoli · {new Date(o.created_at).toLocaleDateString('it-IT')}</div>
                </div>
                <div style={{ textAlign:'right', marginRight:'10px' }}>
                  <div style={{ fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-xl)', fontWeight:'800' }}>{fmt(o.totale)}</div>
                  {saldo > 0.01 ? <div style={{ fontSize:'var(--font-size-xs)', color:'#EF4444', fontWeight:'700' }}>-{fmt(saldo)}</div>
                    : o.stato!=='ANNULLATO' && <div style={{ fontSize:'var(--font-size-xs)', color:'#22C55E', fontWeight:'700' }}>✓ SALDATO</div>}
                </div>
                <span style={{ color: isOpen ? '#F4600C' : 'var(--text-dim)' }}>{isOpen ? '▼' : '▶'}</span>
              </div>
            );
          })}
          {filtered.length === 0 && <div style={{ textAlign:'center', color:'var(--text-dim)', padding:'50px 0' }}>Nessun ordine</div>}
        </div>
      </div>

      {/* ── PANNELLO DETTAGLIO ── */}
      {selected && (
        <div style={s.panel}>
          <div style={s.panelHead}>
            <div>
              <div style={{ fontFamily:"'Barlow Condensed'", fontSize:'var(--font-size-lg)', fontWeight:'800' }}>{selected.numero_ordine}</div>
              <span style={{ ...s.badge, background:`${getStatoColor(selected.stato)}18`, color:getStatoColor(selected.stato), borderColor:`${getStatoColor(selected.stato)}40`, display:'inline-block', marginTop:'6px' }}>{selected.stato}</span>
            </div>
            <button style={{ background:'transparent', border:'none', color:'var(--text-dim)', cursor:'pointer' }} onClick={() => { setSelected(null); setEditMode(false); }}><X size={18}/></button>
          </div>

          <div style={s.panelBody}>
            {/* Cliente */}
            <div style={s.sec}>
              <div style={s.secTitle}>CLIENTE</div>
              <div style={{ fontWeight:'700' }}>{selected.customer?.cognome} {selected.customer?.nome}</div>
              {selected.customer?.email && <div style={s.small}>{selected.customer.email}</div>}
              {selected.customer?.telefono && <div style={s.small}>{selected.customer.telefono}</div>}
            </div>

            {/* Articoli */}
            <div style={s.sec}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
                <div style={s.secTitle}>ARTICOLI ({selected.items?.length})</div>
                {puoModificare && selected.stato === statoIniziale && !editMode && (
                  <button style={{ ...s.btnXs, color:'#3B82F6', borderColor:'#3B82F640', background:'#3B82F615' }} onClick={startEdit}>
                    <Edit2 size={11}/> MODIFICA
                  </button>
                )}
                {editMode && (
                  <div style={{ display:'flex', gap:'5px' }}>
                    <button style={{ ...s.btnXs, color:'#EF4444', borderColor:'#EF444430' }} onClick={() => setEditMode(false)}>ANN.</button>
                    <button style={{ ...s.btnXs, color:'#22C55E', borderColor:'#22C55E40', background:'#22C55E15' }} onClick={saveEdit}><Check size={11}/> SALVA</button>
                  </div>
                )}
              </div>

              {editMode ? (
                <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                  {editItems.map((item, idx) => (
                    <div key={idx} style={{ background:'var(--bg-input)', border:'1px solid var(--border2)', borderRadius:'8px', padding:'10px', display:'flex', flexDirection:'column', gap:'6px' }}>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 48px 30px', gap:'6px' }}>
                        <select style={s.inputXs} value={item.product_id} onChange={e => upd(idx,'product_id',e.target.value)}>
                          <option value="">— prodotto —</option>
                          {products.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
                        </select>
                        <select style={s.inputXs} value={item.variant_id} onChange={e => upd(idx,'variant_id',e.target.value)} disabled={!item.product_id}>
                          <option value="">— taglia —</option>
                          {getVariants(item.product_id).map(v => <option key={v.id} value={v.id}>{v.taglia} ({v.inventory?.quantita_disponibile||0})</option>)}
                        </select>
                        <input style={s.inputXs} type="number" min="1" value={item.quantita} onChange={e => upd(idx,'quantita',e.target.value)} />
                        <button style={{ background:'#EF444415', border:'1px solid #EF444430', borderRadius:'5px', color:'#EF4444', cursor:'pointer' }} onClick={() => setEditItems(p => p.filter((_,i)=>i!==idx))}><X size={11}/></button>
                      </div>
                      <input style={s.inputXs} value={item.note} onChange={e => upd(idx,'note',e.target.value)} placeholder="Note..." />
                    </div>
                  ))}
                  <button style={{ ...s.btnXs, justifyContent:'center', width:'100%' }} onClick={() => setEditItems(p => [...p, { product_id:'', variant_id:'', quantita:1, note:'' }])}>
                    <Plus size={11}/> ARTICOLO
                  </button>
                </div>
              ) : (
                <>
                  {selected.items?.map(item => {
                    const hasStampa = item.note?.includes('STAMPA NOME:');
                    const isPersonalizzato = item.note?.includes('STAMPA NOME:');
                    return (
                      <div key={item.id} style={{ ...s.itemCard, ...(isPersonalizzato ? { borderColor:'#F4600C30' } : {}) }}>
                        <div style={{ display:'flex', justifyContent:'space-between' }}>
                          <div style={{ flex:1 }}>
                            <div style={{ fontWeight:'700', fontSize:'var(--font-size-sm)' }}>{item.variant?.product?.nome}</div>
                            <div style={{ display:'flex', gap:'6px', alignItems:'center', marginTop:'3px' }}>
                              <span style={s.tagliaPill}>{item.variant?.taglia}</span>
                              <span style={{ fontSize: 'var(--font-size-xs)', color:'var(--text-dim)' }}>{item.variant?.sku}</span>
                              {isPersonalizzato && <span style={{ fontSize: 'var(--font-size-xs)', color:'#F4600C', fontWeight:'700' }}>PERS.</span>}
                            </div>
                            {item.note && <div style={{ fontSize:'var(--font-size-xs)', color: hasStampa ? '#F4600C' : 'var(--text-muted)', marginTop:'4px' }}>{hasStampa ? '🖨 ' : '📝 '}{item.note}</div>}
                          </div>
                          <div style={{ textAlign:'right', marginLeft:'10px' }}>
                            <div style={{ fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-lg)', fontWeight:'800' }}>{fmt(item.prezzo_unitario_ivato * item.quantita)}</div>
                            <div style={s.small}>{item.quantita}x {fmt(item.prezzo_unitario_ivato)}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div style={{ display:'flex', justifyContent:'space-between', paddingTop:'10px', borderTop:'1px solid var(--border)', marginTop:'4px' }}>
                    <span style={{ color:'var(--text-dim)', fontSize:'var(--font-size-xs)', fontWeight:'700' }}>TOTALE</span>
                    <span style={{ fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-xl)', fontWeight:'800' }}>{fmt(selected.totale)}</span>
                  </div>
                </>
              )}
            </div>

            {/* Pagamenti */}
            {(selected.payments?.length > 0 || !['ANNULLATO'].includes(selected.stato)) && (
              <div style={s.sec}>
                <div style={s.secTitle}>PAGAMENTI</div>
                {selected.payments?.map(p => (
                  <div key={p.id} style={{ display:'flex', justifyContent:'space-between', padding:'7px 10px', background:'var(--bg-input)', borderRadius:'6px', marginBottom:'5px' }}>
                    <div>
                      <span style={{ fontSize:'var(--font-size-xs)', fontWeight:'700' }}>{p.metodo}</span>
                      {p.note && <span style={{ fontSize:'var(--font-size-xs)', color:'var(--text-dim)', marginLeft:'6px' }}>{p.note}</span>}
                      <div style={{ fontSize: 'var(--font-size-xs)', color:'var(--text-dim)' }}>{new Date(p.data_pagamento).toLocaleDateString('it-IT')}</div>
                    </div>
                    <span style={{ fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-sm)', fontWeight:'800', color:'#22C55E' }}>+{fmt(p.importo)}</span>
                  </div>
                ))}
                {selected.payments?.length > 0 && (
                  <div style={{ display:'flex', justifyContent:'space-between', padding:'5px 2px' }}>
                    <span style={s.small}>SALDO RESIDUO</span>
                    <span style={{ fontFamily:"'Barlow Condensed'", fontWeight:'800', color:(selected.totale-totalePagato(selected))>0.01?'#EF4444':'#22C55E' }}>
                      {fmt(selected.totale-totalePagato(selected))}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* TIMELINE */}
            {selected.history?.length > 0 && (
              <div style={s.sec}>
                <div style={s.secTitle}>TIMELINE ORDINE</div>
                <div style={{ display:'flex', flexDirection:'column', gap:'0' }}>
                  {selected.history.map((h, idx) => {
                    const isLast = idx === selected.history.length - 1;
                    const typeColor = { STATO:'#3B82F6', PAGAMENTO:'#22C55E', ALERT:'#F59E0B', ARTICOLO:'#8B5CF6', NOTA:'var(--text-muted)' }[h.tipo] || 'var(--text-dim)';
                    return (
                      <div key={h.id} style={{ display:'flex', gap:'10px' }}>
                        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', width:'20px', flexShrink:0 }}>
                          <div style={{ width:'10px', height:'10px', borderRadius:'50%', background:typeColor, border:`2px solid ${typeColor}`, marginTop:'4px', flexShrink:0 }} />
                          {!isLast && <div style={{ width:'2px', flex:1, background:'var(--border)', minHeight:'16px', marginTop:'2px' }} />}
                        </div>
                        <div style={{ flex:1, paddingBottom: isLast ? '0' : '10px' }}>
                          <div style={{ fontSize:'var(--font-size-xs)', color:'var(--text)', lineHeight:'1.4' }}>{h.contenuto}</div>
                          <div style={{ fontSize: 'var(--font-size-xs)', color:'var(--text-dim)', marginTop:'2px' }}>
                            {h.user_nome && <span style={{ marginRight:'6px' }}>{h.user_nome}</span>}
                            {new Date(h.created_at).toLocaleString('it-IT', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Note */}
            {selected.note && (
              <div style={s.sec}>
                <div style={s.secTitle}>NOTE</div>
                <div style={{ color:'var(--text-muted)', fontSize:'var(--font-size-sm)' }}>{selected.note}</div>
              </div>
            )}

            {/* AZIONI */}
            <div style={s.sec}>
              <div style={s.secTitle}>AZIONI</div>
              <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                {/* Cambio stato */}
                {puoModificare && getStatiDisponibili(selected.stato).length > 0 && (
                  <div>
                    <label style={{ ...s.secTitle, display:'block', marginBottom:'6px' }}>CAMBIA STATO IN</label>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:'6px' }}>
                      {getStatiDisponibili(selected.stato).map(st => (
                        <button key={st.id}
                          style={{ flex:1, minWidth:'80px', padding:'8px 6px', borderRadius:'7px', border:`1px solid ${st.colore}50`, background:`${st.colore}15`, color:st.colore, fontSize:'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' }}
                          onClick={() => { if(window.confirm(`Passare a "${st.nome}"?`)) changeStatus(selected.id, st.nome); }}>
                          {st.nome}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {/* Paga */}
                {puoModificare && !['ANNULLATO'].includes(selected.stato) && (selected.totale - totalePagato(selected)) > 0.01 && (
                  <button style={{ width:'100%', padding:'10px', borderRadius:'8px', border:'1px solid #22C55E40', background:'#22C55E15', color:'#22C55E', fontSize:'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' }}
                    onClick={() => { setPayModal(selected); setPayForm({ metodo:'CONTANTI', importo:(selected.totale-totalePagato(selected)).toFixed(2), note:'' }); }}>
                    💳 Registra pagamento
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL PAGAMENTO */}
      {payModal && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <div style={{ fontFamily:"'Barlow Condensed'", fontSize:'var(--font-size-lg)', fontWeight:'700', letterSpacing:'2px', marginBottom:'4px' }}>PAGAMENTO</div>
            <div style={{ color:'var(--text-dim)', fontSize:'var(--font-size-xs)', marginBottom:'20px' }}>{payModal.numero_ordine} — {fmt(payModal.totale)}</div>
            <form onSubmit={handlePay} style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
              <div style={s.field}><label style={s.label}>METODO</label>
                <select style={s.input} value={payForm.metodo} onChange={e=>setPayForm(f=>({...f,metodo:e.target.value}))}>
                  {['CONTANTI','BONIFICO','SATISPAY','STAFF','VOUCHER','POS'].map(m=><option key={m}>{m}</option>)}
                </select></div>
              <div style={s.field}><label style={s.label}>IMPORTO</label>
                <input style={s.input} type="number" step="0.01" value={payForm.importo} onChange={e=>setPayForm(f=>({...f,importo:e.target.value}))} required /></div>
              <div style={s.field}><label style={s.label}>NOTE</label>
                <input style={s.input} value={payForm.note} onChange={e=>setPayForm(f=>({...f,note:e.target.value}))} /></div>
              <div style={{ display:'flex', gap:'10px', justifyContent:'flex-end' }}>
                <button type="button" style={s.btnSec} onClick={()=>setPayModal(null)}>ANNULLA</button>
                <button type="submit" style={s.btnPri}>CONFERMA</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  title: { fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-title)', fontWeight:'800', letterSpacing:'2px' },
  sub: { color:'var(--text-dim)', fontSize:'var(--font-size-sm)', marginTop:'4px' },
  btnPri: { display:'flex', alignItems:'center', gap:'8px', background:'#F4600C', color:'#fff', border:'none', borderRadius:'8px', padding:'11px 18px', fontSize:'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' },
  btnSec: { background:'var(--border)', color:'var(--text)', border:'1px solid var(--border2)', borderRadius:'8px', padding:'9px 14px', fontSize:'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' },
  btnXs: { display:'flex', alignItems:'center', gap:'5px', background:'transparent', border:'1px solid var(--border2)', borderRadius:'6px', padding:'4px 9px', color:'var(--text-muted)', fontSize:'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' },
  filterBtn: { padding:'6px 12px', borderRadius:'6px', border:'1px solid var(--border2)', background:'var(--bg-card)', color:'var(--text-muted)', fontSize:'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' },
  filterActive: { background:'#F4600C', color:'#fff', borderColor:'#F4600C' },
  orderRow: { display:'flex', alignItems:'center', gap:'12px', background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'10px', padding:'14px 16px', cursor:'pointer' },
  orderRowSel: { borderColor:'#F4600C50', background:'#FFF1E8' },
  badge: { padding:'2px 8px', borderRadius:'4px', fontSize: 'var(--font-size-xs)', fontWeight:'700', border:'1px solid' },
  panel: { width:'400px', flexShrink:0, background:'var(--bg)', border:'1px solid var(--border)', borderRadius:'12px', display:'flex', flexDirection:'column', position:'sticky', top:'20px', maxHeight:'calc(100vh - 100px)', overflow:'hidden' },
  panelHead: { display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'18px 20px 14px', borderBottom:'1px solid var(--border)' },
  panelBody: { padding:'14px 16px 20px', overflowY:'auto', flex:1, display:'flex', flexDirection:'column', gap:'10px' },
  sec: { background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'10px', padding:'12px 14px' },
  secTitle: { fontSize: 'var(--font-size-xs)', letterSpacing:'2px', fontWeight:'700', color:'var(--text-dim)', marginBottom:'8px' },
  itemCard: { background:'var(--bg-input)', border:'1px solid #222', borderRadius:'8px', padding:'10px 12px', marginBottom:'6px' },
  tagliaPill: { background:'var(--border)', border:'1px solid var(--border2)', borderRadius:'4px', padding:'2px 8px', fontSize:'var(--font-size-xs)', fontWeight:'700', color:'var(--text-muted)' },
  small: { color:'var(--text-dim)', fontSize:'var(--font-size-xs)', marginTop:'2px' },
  overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 },
  modal: { background:'var(--bg-card)', border:'1px solid #222', borderRadius:'12px', padding:'28px', minWidth:'360px' },
  field: { display:'flex', flexDirection:'column', gap:'6px' },
  label: { fontSize: 'var(--font-size-xs)', letterSpacing:'2px', fontWeight:'700', color:'var(--text-dim)' },
  input: { background:'var(--bg-input)', border:'1px solid var(--border2)', borderRadius:'8px', padding:'9px 12px', color:'var(--text)', fontSize:'var(--font-size-sm)', outline:'none' },
  inputXs: { background:'var(--bg-card)', border:'1px solid var(--border2)', borderRadius:'6px', padding:'6px 8px', color:'var(--text)', fontSize:'var(--font-size-xs)', outline:'none', width:'100%', boxSizing:'border-box' },
};
